import { ArrowBack } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  ButtonVariants,
  ModalContentContainer,
  TextField,
} from '@/components';
import { Routes } from '@/constants';
import { useForm } from '@/hooks';
import { useSendResetPasswordEmailMutation } from '@/store';
import { SnackbarUtils } from '@/utils';

import { PageContainer } from '../components';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { form, errors, setErrors, validateForm, onChange } = useForm({
    email: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sendResetPasswordEmail] = useSendResetPasswordEmailMutation();

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    try {
      await sendResetPasswordEmail({
        email: form.email.trim(),
      }).unwrap();
      SnackbarUtils.success(`Email sent to ${form.email.trim()}`);
      setIsSubmitted(true);
    } catch (error) {
      const { status, data } = error as {
        status: number;
        data: { message: string };
      };
      if (status === 400 && data?.message) {
        setErrors(prev => ({
          ...prev,
          email: data.message,
        }));
      } else {
        setErrors({
          email: 'Something went wrong. Please try again or contact support.',
        });
      }
    }
  };

  return (
    <PageContainer hideCertificationInfo>
      <ModalContentContainer>
        {!isSubmitted ? (
          <>
            <Typography variant="h1">Can&apos;t Log In?</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Enter the email you used to sign up, or a recovery email, and
              we&apos;ll send instructions to help you get back into your
              account.
            </Typography>
            <TextField
              id="email"
              value={form.email}
              fullWidth
              label="Email"
              placeholder="Enter your email"
              type="email"
              error={!!errors.email}
              helperText={errors.email}
              sx={{ mt: 2 }}
              onChange={onChange}
            />
            <Button
              variant="primary"
              label="Send Instructions"
              fullWidth
              sx={{ mt: 6, height: 48, fontSize: 16 }}
              onClick={onSubmit}
            />
            <Button
              label="Go Back"
              onClick={() => {
                navigate(Routes.LOGIN);
              }}
              variant={ButtonVariants.PLAIN}
              skinny={false}
              fullWidth={false}
              startIcon={<ArrowBack />}
              sx={{ mt: 2 }}
            />
          </>
        ) : (
          <>
            <Typography variant="h1">Check your email</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              If there is an account associated with{' '}
              <strong>{form.email}</strong> you will receive an email with
              instructions for resetting your password.
            </Typography>
            <Button
              variant={ButtonVariants.PLAIN}
              label="Resend Email"
              fullWidth={false}
              skinny={false}
              sx={{ mt: 2 }}
              onClick={onSubmit}
            />
          </>
        )}
      </ModalContentContainer>
    </PageContainer>
  );
};
